<script setup lang="ts">
import { computed } from "vue";
import { t } from "@/plugins/i18n";
import PeriodDimensionDayOfWeek from "@/modules/explorer/components/SecondaryDrawer/PeriodDimensionDayOfWeek.vue";
import PeriodDimensionHoursOfDay from "@/modules/explorer/components/SecondaryDrawer/PeriodDimensionHoursOfDay.vue";
import PeriodDimensionMinute from "@/modules/explorer/components/SecondaryDrawer/PeriodDimensionMinute.vue";
import DateSelectionWrapper from "@/modules/explorer/components/DateSelectionWrapper.vue";
import PolarityToggle from "./PolarityToggle.vue";
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import PeriodSelectionDTO = App.Data.Explorer.PeriodSelectionDTO;

const { dimension } = defineProps<{
	dimension: DocumentDimensionDTO;
}>();

const values = defineModel<FilterValueDTO[]>("values", { required: true });
const included = defineModel<boolean>("included", { required: true });

const granularity = computed(() => dimension.granularity);

/** There is no "everything outside this date range" to ask for. */
const hasPolarity = computed(() => granularity.value !== "day");

const period = computed(() => values.value[0]?.filter_period ?? undefined);

/**
 * Keeps the id and placeholder label the query expects; the label never shows,
 * as the filter chip reads period filters off the dimension.
 */
const setPeriod = (next: PeriodSelectionDTO) => {
	values.value = [
		{
			id: 1,
			level: null,
			selection_depth: null,
			model: null,
			sort_order: null,
			label: "n/a",
			filter_period: next,
		},
	];
};

const isSupported = computed(() =>
	["dayofweek", "60min", "1min", "day"].includes(granularity.value ?? "")
);
</script>
<template>
	<section class="zone">
		<header class="zone-header">
			<h6 class="zone-label">{{ t("filterDialog.period.heading") }}</h6>
			<polarity-toggle v-if="hasPolarity && isSupported" v-model="included" />
		</header>
		<p v-if="!isSupported" class="text-body-2 muted mb-0">
			{{ t("filterDialog.period.unsupported") }}
		</p>
		<template v-else>
			<!-- Keyed: each editor copies modelValue on mount, so switching to another
			dimension of the same granularity has to remount it or it keeps the old
			selection while the draft has been reset. -->
			<period-dimension-day-of-week
				v-if="granularity === 'dayofweek'"
				:key="dimension.name_clean"
				v-model="values"
			/>
			<period-dimension-hours-of-day
				v-else-if="granularity === '60min'"
				:key="dimension.name_clean"
				v-model="values"
			/>
			<period-dimension-minute
				v-else-if="granularity === '1min'"
				:key="dimension.name_clean"
				v-model="values"
			/>
			<div v-else class="range">
				<date-selection-wrapper
					:key="dimension.name_clean"
					:model-value="period"
					:show-presets="true"
					:show-apply="false"
					@update:model-value="setPeriod"
				/>
			</div>
		</template>
	</section>
</template>
<style scoped lang="scss">
.zone {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.zone-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
}

.zone-label {
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: rgb(var(--color-on-background));
	margin-bottom: 0;
}

.muted {
	color: rgb(var(--color-on-background));
}

.range {
	border: 1px solid rgb(var(--color-secondary));
	border-radius: 4px;
	overflow: hidden;
}
</style>
